/**
 * Pure helpers behind the Legislation tab. No React, no DOM: shared by the Worker
 * (worker/src/legislation.js builds the snapshots with them) and the client, and covered by
 * tests/legislation.test.ts.
 */
import { LIKELY, UNLIKELY, projectRollCall, type Forecast, type ForecastFactor, type ForecastInput, type ForecastLabel, type Position } from "../forecast/forecast";
import type { NewFactorId } from "../forecast/model";
import type { BacktestRow, CouncilForecast, FiledItem, InfluenceRow, MemberPrediction } from "./types";

/** Who the forecast is read for: a client ("their way"), the viewer ("your way"), or a plain yes. */
export type Perspective = "their" | "your" | "yes";

export const labelOf = (p: number): ForecastLabel => (p >= LIKELY ? "likely" : p <= UNLIKELY ? "unlikely" : "tossup");

/** The same forecast seen from the opposite position (p → 1 − p, range swapped, factors negated). */
export function complement(f: Forecast): Forecast {
  const p = 1 - f.p;
  return { ...f, p, low: 1 - f.high, high: 1 - f.low, label: labelOf(p), factors: f.factors.map((x) => ({ ...x, effect: -x.effect })) };
}

/**
 * Forecasts are stored relative to the lobbying client's position ("their way").
 * - "their": as stored
 * - "your":  flipped if the viewer's position differs from the client's
 * - "yes":   flipped if the client opposes
 */
export function inPerspective(f: Forecast, perspective: Perspective, clientPosition: Position, myPosition?: Position): Forecast {
  if (perspective === "their") return f;
  const target: Position = perspective === "yes" ? "support" : myPosition ?? clientPosition;
  return target === clientPosition ? f : complement(f);
}

export type WhatIf = Record<string /* personId */, "yea" | "nay">;

/** A member's yes-probability from a client-relative forecast. */
export const pYesOf = (r: InfluenceRow, clientPosition: Position) => (clientPosition === "support" ? r.forecast.p : 1 - r.forecast.p);

/** Projected roll call with What-if overrides applied (nothing is persisted). */
export function projectWithWhatIf(rows: InfluenceRow[], clientPosition: Position, overrides: WhatIf, needed: number) {
  const pYes = rows.map((r) => {
    const o = overrides[r.personId];
    if (o) return o === "yea" ? 1 : 0;
    return pYesOf(r, clientPosition);
  });
  return projectRollCall(pYes, needed);
}

/** The council card ("Needs 26 yes votes. Projected 27–23…"), from every member's forecast. */
export function councilForecast(rows: InfluenceRow[], clientPosition: Position, needed: number, tension: number, topic: string): CouncilForecast {
  const p = projectWithWhatIf(rows, clientPosition, {}, needed);
  return { projectedYes: p.yes, projectedNo: p.no, range: p.range, needed, likelyYes: p.likelyYes, tossup: p.tossup, likelyNo: p.likelyNo, tension, topic };
}

/** Council chip: "Likely passes" / "Likely fails" / "Toss-up" (3+ votes either side of what it needs). */
export const councilLabel = (f: CouncilForecast): ForecastLabel =>
  f.projectedYes >= f.needed + 3 && f.range[0] >= f.needed ? "likely" : f.projectedYes <= f.needed - 3 && f.range[1] < f.needed ? "unlikely" : "tossup";

export type MemberSort = "persuadable" | "money" | "forecast";

/** Stable sort. "persuadable" = closest to 50% first (the toss-ups the whip count cares about). */
export function sortMembers(rows: InfluenceRow[], by: MemberSort): InfluenceRow[] {
  const key = (r: InfluenceRow) =>
    by === "persuadable" ? Math.abs(r.forecast.p - 0.5) : by === "money" ? -r.sinceAmount : -r.forecast.p;
  return rows.map((r, i) => ({ r, i })).sort((a, b) => key(a.r) - key(b.r) || a.i - b.i).map((x) => x.r);
}

/** Members grouped the way the Forecast-by-member tab shows them. */
export function groupByLabel(rows: InfluenceRow[], perspective: Perspective, clientPosition: Position, myPosition?: Position) {
  const out: Record<ForecastLabel, { row: InfluenceRow; f: Forecast }[]> = { tossup: [], unlikely: [], likely: [] };
  for (const row of rows) {
    const f = inPerspective(row.forecast, perspective, clientPosition, myPosition);
    out[f.label].push({ row, f });
  }
  return out;
}

/** "+6 pts" tag after a live forecast change; nothing under 1 point. */
export function movedPoints(prev: number | undefined, next: number): number | null {
  if (prev === undefined) return null;
  const d = Math.round((next - prev) * 100);
  return Math.abs(d) >= 1 ? d : null;
}

/** Roll-calls tab margin bar. */
export const margin = (yea: number, nay: number) => ({ yeaShare: yea + nay ? yea / (yea + nay) : 0.5, apart: Math.abs(yea - nay) });

export type FiledRow = { kind: "item"; item: FiledItem } | { kind: "group"; key: string; items: FiledItem[] };

/** Filed tab: collapse near-identical filings (same sponsor + same kind of item) into one group row. */
export function groupFiled(items: FiledItem[]): FiledRow[] {
  const out: FiledRow[] = [];
  const groups = new Map<string, FiledItem[]>();
  for (const it of items) {
    if (!it.similarKey) { out.push({ kind: "item", item: it }); continue; }
    if (!groups.has(it.similarKey)) { groups.set(it.similarKey, []); out.push({ kind: "group", key: it.similarKey, items: groups.get(it.similarKey)! }); }
    groups.get(it.similarKey)!.push(it);
  }
  // a "group" of one is just an item
  return out.map((x) => (x.kind === "group" && x.items.length === 1 ? { kind: "item" as const, item: x.items[0] } : x));
}

/** Forecast-accuracy tab: reliability buckets (bar = what happened, tick = mean forecast). */
export function calibrate(preds: MemberPrediction[], edges = [0.1, 0.2, 0.35, 0.5, 0.65, 0.8, 0.95]) {
  const buckets = edges.slice(0, -1).map((lo, i) => ({ lo, hi: edges[i + 1], n: 0, sumP: 0, hits: 0 }));
  for (const { p, votedThatWay } of preds) {
    const b = buckets.find((x) => p >= x.lo && p < x.hi);
    if (!b) continue;
    b.n++; b.sumP += p; if (votedThatWay) b.hits++;
  }
  return buckets.filter((b) => b.n > 0).map((b) => ({
    label: `${Math.round(b.lo * 100)}–${Math.round(b.hi * 100)}%`, n: b.n, expected: b.sumP / b.n, actual: b.hits / b.n,
  }));
}

/** Headline numbers for the accuracy tab. */
export function accuracySummary(tests: BacktestRow[]) {
  const misses = tests.map((t) => Math.abs(t.yea - t.forecastYea));
  const sorted = [...misses].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  const worst = sorted.length ? sorted[sorted.length - 1] : 0;
  return {
    called: tests.filter((t) => t.passed === t.forecastPassed).length,
    total: tests.length,
    medianMiss: sorted.length ? (sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2) : 0,
    worstMiss: worst,
    worstAt: tests[misses.indexOf(worst)]?.date,
  };
}

/** List-row forecast signal: "YOU SUPPORT · FORECAST" + "Toss-up · 27–23 est." (projected tally, from the council forecast). */
export function rowSignal(f: CouncilForecast, myPosition?: Position) {
  const pYes = f.projectedYes / Math.max(1, f.projectedYes + f.projectedNo);
  const label = labelOf(myPosition === "oppose" ? 1 - pYes : pYes);
  return {
    label: myPosition ? `You ${myPosition} · forecast` : "No position · forecast",
    perspective: (myPosition ? "your" : "yes") as Perspective,
    forecastLabel: label,
    tally: `${f.projectedYes}–${f.projectedNo}${label === "tossup" || !myPosition ? " est." : ""}`,
  };
}

const signed2 = (x: number) => `${x >= 0 ? "+" : "−"}${Math.abs(x).toFixed(2)}`;
const leanWord = (x: number) => (x < -0.33 ? "Progressive" : x > 0.33 ? "Conservative" : "Moderate");

/**
 * The "Why" sentences, written from the same inputs forecastVote() used (screenshots/26: "Voted their way
 * 2 of 2 times after receiving CAA money…"). Factual wording only (§2.8): never "bought" or "owned".
 */
export function factorDetails(i: ForecastInput, clientAbbr: string, topic: string, positionStated = true): Record<ForecastFactor["id"], string> {
  const { k, n } = i.moneyLinked;
  const yes = Math.round(i.similar.yesRate * 100);
  const toward = (p: number) => (p >= 0.5 ? "a yes vote" : "a no vote");
  const lean = 1 - Math.abs(i.memberLean - i.billLean);
  const billWord = i.billLean < -0.33 ? "a progressive-leaning bill" : i.billLean > 0.33 ? "a conservative-leaning bill" : "a moderate bill";
  return {
    moneyLinked: !positionStated ? `None yet. ${clientAbbr} hasn't stated a position on any item that got a roll call, so this counts for nothing.`
      : n === 0 ? `No roll calls where ${clientAbbr} filed a position after giving this member money.`
      : `Voted their way ${k} of ${n} time${n === 1 ? "" : "s"} after receiving ${clientAbbr} money.${n < 2 ? " Too few to count." : n < 4 ? ` Only ${n} votes, so this counts for little.` : ""}`,
    leaning: `${leanWord(i.memberLean)} (${signed2(i.memberLean)}) vs. ${billWord} (${signed2(i.billLean)}). That distance points to ${toward(lean)}.`,
    similar: `Voted yes on ${yes}% of ${i.similar.n} similar ${topic} items, which points to ${toward(i.similar.yesRate)}.`,
    tension: `${i.tension >= 0.5 ? "High" : i.tension >= 0.25 ? "Medium" : "Low"} (${i.tension.toFixed(2)}). Members ${i.tension >= 0.5 ? "split by bloc and leaning" : "partly follow the council's usual outcome"} (${Math.round(i.councilYesRate * 100)}% yes), which pulls toward ${i.councilYesRate >= 0.5 ? "yes" : "no"}.`,
    ...evidenceDetails(i, clientAbbr),
  };
}

/** Sentences for the learned factors, written only from the evidence given (yes frame: "points to a yes vote"). Copy pending designer review. */
export function evidenceDetails(i: ForecastInput, clientAbbr: string): Record<NewFactorId, string> {
  const c = i.committee, m = i.mayor, wv = i.wardVoice, pr = i.procedure, we = i.wardElectorate, ms = i.mayorStatement;
  const pct = (x: number) => `${Math.round(x * 100)}%`;
  const topicWord = i.saidTopic != null ? (i.saidTopic > 0.2 ? "supported" : i.saidTopic < -0.2 ? "opposed" : "took mixed positions on") : null;
  return {
    sponsor: i.sponsor === "lead" ? "Lead sponsor of this item, which points to a yes vote." : i.sponsor === "co" ? "Co-sponsored this item, which points to a yes vote." : "Not a sponsor of this item.",
    committee: !c || !c.rec ? "No committee action on record yet."
      : `${c.member ? "Sits on the committee that " : "The committee "}${c.rec > 0 ? "recommended it pass" : "recommended against it"}${c.margin >= 0.99 ? " unanimously" : c.margin >= 0.5 ? " by a wide margin" : " narrowly"}, which points to ${c.rec > 0 ? "a yes vote" : "a no vote"}.`,
    said: i.said ? `Spoke ${i.said === "support" ? "for" : "against"} this item on the record, which points to ${i.said === "support" ? "a yes vote" : "a no vote"}.`
      : topicWord ? `On the record, ${topicWord} similar items in the past year.` : "Nothing on the record about this item yet.",
    mayor: !m?.sponsored ? "Not a Mayor-sponsored item." : m.n ? `Voted yes on ${m.k} of ${m.n} divided Mayor-sponsored items (council: ${pct(m.councilRate)}).` : "A Mayor-sponsored item; no divided Mayor-sponsored votes on record for this member yet.",
    allies: i.allies == null || Math.abs(i.allies) < 0.05 ? "No close colleague has taken a position yet." : `Colleagues who usually vote with this member have ${i.allies > 0 ? "lined up behind" : "come out against"} this item.`,
    wardVoice: wv && wv.support + wv.oppose >= MIN_WARD_VOICE ? `Verified residents of the ward said ${pct(wv.support / (wv.support + wv.oppose))} support (${wv.support + wv.oppose} answers).` : "Not enough verified residents of the ward have answered yet.",
    moneyAmount: i.moneyAmount == null || Math.abs(i.moneyAmount) < 0.01 ? `No money in the past year from clients with a stated position.`
      : `Received money in the past year from clients who stated a position (${i.moneyAmount > 0 ? "supporters" : "opponents"}, ${clientAbbr} among them). Money near a vote is not evidence that it influenced the vote.`,
    graph: i.graph == null || Math.abs(i.graph) < 0.05 ? "No notable relationship-graph connection to this client." : `${i.graph > 0 ? "Close to" : "Far from"} ${clientAbbr} in the relationship graph (shared donors, lobbyists and sponsorships).`,
    homeWard: i.homeWard === "own" ? "The item is about this member's own ward, which points to a yes vote." : "Not an item about this member's ward.",
    procedure: !pr || !(pr.deferred || pr.substituted || pr.reReferred || (pr.daysInCommittee ?? 0) > 180) ? "Moved through committee without being deferred or substituted."
      : `${[pr.deferred && "deferred and published", pr.substituted && "substituted", pr.reReferred && "re-referred", (pr.daysInCommittee ?? 0) > 180 && `${Math.round((pr.daysInCommittee ?? 0) / 30)} months in committee`].filter(Boolean).join(", ")}: contested items move like this.`,
    wardElectorate: !we?.mayorSponsored ? "Not a Mayor-sponsored item." : we.johnsonShare == null ? "No 2023 runoff result on file for this ward yet."
      : `The ward gave the Mayor ${pct(we.johnsonShare)} in the 2023 runoff.`,
    wardDemographics: i.wardDemographics?.value == null ? "No ward demographic applies to this item." : `${i.wardDemographics.variable || "The ward"} is ${Math.abs(i.wardDemographics.value) < 0.5 ? "near" : i.wardDemographics.value > 0 ? "above" : "below"} the city's typical ward.`,
    leaning2: typeof i.memberLean2 !== "number" || typeof i.billLean2 !== "number" ? "No second axis yet."
      : `On the council's second axis this member sits ${Math.abs(i.memberLean2 - i.billLean2) < 0.25 ? "close to" : "away from"} the members who voted yes on similar items.`,
    mayorStatement: !ms || !ms.videos ? "The Mayor has not spoken about this item on the channel."
      : `The Mayor spoke about this item in ${ms.videos} video${ms.videos === 1 ? "" : "s"}; this member voted with the Mayor on ${ms.k} of ${ms.n} divided Mayor-sponsored items.`,
  };
}

/** "Your ward said" (offices, 43): fewer than this many verified residents reads "Not enough yet" (starter default). */
export const MIN_WARD_VOICE = 25;
export function wardSaid(v: { support: number; oppose: number }, min = MIN_WARD_VOICE) {
  const n = v.support + v.oppose;
  const share = n ? Math.round((v.support / n) * 100) : 0;
  return { n, share, enough: n >= min, tone: n < min ? "neutral" : share >= 50 ? "positive" : "negative" } as const;
}

/** The resident's alderperson card line (screenshots/42): "Likely to vote yes on 4 of your 5 items on Oct 14. One is a toss-up: …". */
export function alderSummary(items: { shortTitle?: string; title: string; myAlderperson?: { forecast?: Forecast } }[], date: string) {
  const known = items.filter((i) => i.myAlderperson?.forecast).map((i) => ({ ...i, alderYes: i.myAlderperson!.forecast }));
  if (!known.length) return "";
  const yes = known.filter((i) => i.alderYes!.label === "likely").length;
  const toss = known.filter((i) => i.alderYes!.label === "tossup").map((i) => (i.shortTitle || i.title).replace(/^./, (c) => c.toLowerCase()));
  const words = ["", "One", "Two", "Three", "Four", "Five"];
  const tail = toss.length === 0 ? "" : toss.length === 1 ? ` One is a toss-up: the ${toss[0]}.` : ` ${words[toss.length] ?? toss.length} are toss-ups: the ${toss.slice(0, -1).join(", the ")} and the ${toss[toss.length - 1]}.`;
  return `Likely to vote yes on ${yes} of your ${known.length} items on ${date}.${tail}`;
}
